/**
 * CTRL_14 Dashboard - Data Minimization
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Progress } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  MinusCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

function CTRL_14_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_14';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate minimization coverage
  const minimizationCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, minimized: 0, percentage: 0 };

    const minimized = events.filter(e => {
      const metadata = e.event_metadata || {};
      return metadata.data_minimized === true;
    }).length;

    return {
      total: events.length,
      minimized,
      percentage: Math.round((minimized / events.length) * 100)
    };
  }, [events]);

  // Calculate feature removal stats
  const featureRemovalStats = useMemo(() => {
    const totalFeaturesRemoved = events.reduce((sum, e) => {
      return sum + (e.event_metadata?.features_removed_count || 0);
    }, 0);

    const eventsWithRemoval = events.filter(e =>
      e.event_metadata?.unnecessary_features_removed === true
    ).length;

    return {
      totalRemoved: totalFeaturesRemoved,
      eventsWithRemoval,
      avgRemoved: events.length > 0 ? (totalFeaturesRemoved / events.length).toFixed(1) : 0
    };
  }, [events]);

  // Calculate retention policy compliance
  const retentionCompliance = useMemo(() => {
    const withPolicy = events.filter(e =>
      e.event_metadata?.retention_policy_applied === true
    ).length;

    const retentionDays = events
      .map(e => e.event_metadata?.retention_days)
      .filter(days => days !== undefined && days !== null);

    const avgRetention = retentionDays.length > 0 ?
      (retentionDays.reduce((sum, days) => sum + days, 0) / retentionDays.length).toFixed(0) : 0;

    return {
      withPolicy,
      percentage: events.length > 0 ? Math.round((withPolicy / events.length) * 100) : 0,
      avgRetention
    };
  }, [events]);

  // Calculate feature relevance
  const relevanceStats = useMemo(() => {
    const relevant = events.filter(e =>
      e.event_metadata?.features_relevant === true
    ).length;

    return {
      relevant,
      percentage: events.length > 0 ? Math.round((relevant / events.length) * 100) : 0
    };
  }, [events]);

  // Table columns
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (ts) => new Date(ts).toLocaleString(),
      width: 180
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      ellipsis: true
    },
    {
      title: 'Data Minimized',
      key: 'minimized',
      align: 'center',
      render: (_, record) => {
        const minimized = record.event_metadata?.data_minimized === true;
        return minimized ?
          <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag> :
          <Tag color="red" icon={<WarningOutlined />}>No</Tag>;
      }
    },
    {
      title: 'Features Removed',
      dataIndex: ['event_metadata', 'features_removed_count'],
      key: 'removed',
      align: 'center',
      render: (count) => count !== undefined ? <Tag color="blue">{count}</Tag> : '-'
    },
    {
      title: 'Retention Days',
      dataIndex: ['event_metadata', 'retention_days'],
      key: 'retention',
      align: 'center',
      render: (days) => {
        if (days === undefined || days === null) return '-';

        let color = '#52c41a';
        if (days > 365) color = '#faad14';
        if (days > 730) color = '#f5222d';

        return <Tag color={color} icon={<ClockCircleOutlined />}>{days}</Tag>;
      }
    },
    {
      title: 'Features Relevant',
      key: 'relevant',
      align: 'center',
      render: (_, record) => {
        const relevant = record.event_metadata?.features_relevant === true;
        return relevant ?
          <Tag color="green">Yes</Tag> :
          <Tag color="orange">Unknown</Tag>;
      }
    },
    {
      title: 'Retention Policy',
      key: 'policy',
      align: 'center',
      render: (_, record) => {
        const applied = record.event_metadata?.retention_policy_applied === true;
        return applied ?
          <Tag color="green">Applied</Tag> :
          <Tag color="red">Missing</Tag>;
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <Empty
        description="Please select a project to view dashboard"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_14: Data Minimization Dashboard</h2>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Minimization Coverage"
              value={minimizationCoverage.percentage}
              suffix="%"
              prefix={minimizationCoverage.percentage >= 90 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: minimizationCoverage.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {minimizationCoverage.minimized} of {minimizationCoverage.total} minimized
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Features Removed"
              value={featureRemovalStats.totalRemoved}
              prefix={<DeleteOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              Avg: {featureRemovalStats.avgRemoved} per event
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Retention Policy"
              value={retentionCompliance.percentage}
              suffix="%"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: retentionCompliance.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              Avg: {retentionCompliance.avgRetention} days
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Feature Relevance"
              value={relevanceStats.percentage}
              suffix="%"
              prefix={<MinusCircleOutlined />}
              valueStyle={{ color: relevanceStats.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Minimization Compliance Overview */}
      <Card title="Minimization Compliance Overview" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ backgroundColor: '#f6ffed', borderColor: '#52c41a' }}>
              <Statistic
                title="Data Minimized"
                value={minimizationCoverage.minimized}
                suffix={`/ ${minimizationCoverage.total}`}
                valueStyle={{ color: '#52c41a' }}
              />
              <Progress
                percent={minimizationCoverage.percentage}
                strokeColor="#52c41a"
                size="small"
                style={{ marginTop: '8px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ backgroundColor: '#e6f7ff', borderColor: '#1890ff' }}>
              <Statistic
                title="Retention Policy Applied"
                value={retentionCompliance.withPolicy}
                suffix={`/ ${events.length}`}
                valueStyle={{ color: '#1890ff' }}
              />
              <Progress
                percent={retentionCompliance.percentage}
                strokeColor="#1890ff"
                size="small"
                style={{ marginTop: '8px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ backgroundColor: '#fff7e6', borderColor: '#fa8c16' }}>
              <Statistic
                title="Features Relevant"
                value={relevanceStats.relevant}
                suffix={`/ ${events.length}`}
                valueStyle={{ color: '#fa8c16' }}
              />
              <Progress
                percent={relevanceStats.percentage}
                strokeColor="#fa8c16"
                size="small"
                style={{ marginTop: '8px' }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Data Minimization Table */}
      <Card title="Data Minimization Tracking">
        <Table
          dataSource={events.slice(0, 20)}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} events`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_14_Dashboard;
